/**
 * Modelo de navegación del sidebar — SYSTEM_DESIGN.md §4 / INC-07.
 *
 * Antes cada ítem era JSX escrito a mano (~55 líneas por grupo, 990 en total,
 * con 14 useRef numerados para los acordeones). Acá es data: el renderer lo
 * recorre. Agregar una sección es agregar un objeto.
 */
import { FeatureFlagKey } from "../models/featureFlag";

export interface NavItem {
  label: string;
  to: string;
  /** Clase de PrimeIcons. Hoy no lo usa ningún ítem: los iconos son de grupo. */
  icon?: string;
  /** Solo visible para SUPER_ADMIN. */
  superAdminOnly?: boolean;
  /** Solo visible cuando el flag del tenant está habilitado. */
  featureFlagKey?: FeatureFlagKey;
}

export interface NavGroup {
  label: string;
  icon: string;
  /** Grupo con acordeón. */
  items?: NavItem[];
  /** Enlace directo, sin acordeón. */
  to?: string;
  superAdminOnly?: boolean;
  /** Solo visible cuando el flag del tenant está habilitado. */
  featureFlagKey?: FeatureFlagKey;
}

export const NAV_MODEL: NavGroup[] = [
  {
    label: "Eventos",
    icon: "pi pi-calendar",
    items: [
      { label: "Lista de eventos", to: "/events" },
      { label: "Editar orden destacados", to: "/events/featured-order" },
      { label: "Agregar evento", to: "/events/new" },
      { label: "Historial de cancelaciones", to: "/events/cancellations" },
      // GTK-748 — Asistencias cuelga del acordeón de Eventos, como el diseño
      // aprobado: la pantalla se presenta como "Inicio › Eventos › Asistencia".
      // El gate por flag viaja con el ítem (NavItem.featureFlagKey), así que
      // sigue oculta para los tenants que no lo tienen habilitado.
      {
        label: "Asistencias",
        to: "/attendances",
        featureFlagKey: "attendance_bo_section_enabled",
      },
      {
        label: "Tomar asistencia",
        to: "/attendances/toma",
        featureFlagKey: "attendance_bo_section_enabled",
      },
      // "Gestión de sub-tipos" (/events/types) salió del menú en el PR #173
      // (GTK-701): el ABM de tipos y subtipos vive en Configuración → Gestión
      // de tipos. La ruta sigue existiendo, solo no se ofrece desde acá.
    ],
  },
  {
    label: "Usuarios",
    icon: "pi pi-users",
    items: [
      { label: "Lista de usuarios", to: "/users" },
      { label: "Crear usuario ficticio", to: "/users/new" },
      { label: "Cuentas eliminadas", to: "/users/deleted" },
      { label: "Enviar push notifications", to: "/notifications/send" },
      { label: "Enviar mensajes personalizados", to: "/notifications/custom-messages" },
      { label: "Historial de notificaciones", to: "/notifications/history" },
    ],
  },
  {
    label: "Tenants",
    icon: "pi pi-building",
    items: [
      { label: "Listado de tenants", to: "/tenants" },
      { label: "Nuevo tenant", to: "/tenants/new", superAdminOnly: true },
    ],
  },
  {
    label: "Usuarios BO",
    icon: "pi pi-id-card",
    to: "/bo-users",
    superAdminOnly: true,
  },
  {
    label: "Venues",
    icon: "pi pi-map-marker",
    items: [
      { label: "Listado de venues", to: "/venues" },
      { label: "Nuevo venue", to: "/venues/new" },
    ],
  },
  {
    label: "Talleres",
    icon: "pi pi-book",
    items: [
      { label: "Listado de talleres", to: "/workshops" },
      { label: "Nuevo taller", to: "/workshops/new" },
    ],
  },
  {
    label: "Beneficios",
    icon: "pi pi-tag",
    items: [
      { label: "Configuración", to: "/benefits-settings" },
      { label: "Listado de beneficios", to: "/benefits-catalog" },
      { label: "Partners", to: "/benefits-partners" },
      { label: "Categorías", to: "/benefits-categories" },
    ],
  },
  // GTK-884 / GTK-885: grupo "Puntos" (decisión de Producto: todo lo de Chispas va acá, no en
  // Configuración).
  {
    label: "Puntos",
    icon: "pi pi-bolt",
    featureFlagKey: "gamification_points_enabled",
    items: [
      { label: "Dashboard", to: "/points/metrics" },
      { label: "Reglas", to: "/rules/points" },
      { label: "Catálogo de canjes", to: "/points/rewards" },
      { label: "Canjes", to: "/points/redemptions" },
      // "Movimientos" y no "Descuentos": el selector de signo también muestra las
      // acreditaciones, aunque la pantalla arranque en los descuentos.
      { label: "Movimientos", to: "/points/ledger" },
      { label: "Configuración", to: "/points/settings" },
    ],
  },
  {
    label: "Coms",
    icon: "pi pi-megaphone",
    items: [
      { label: "Lista de comunicaciones", to: "/institutional-communications" },
      { label: "Agregar comunicación", to: "/institutional-communications/new" },
    ],
  },
  {
    label: "Moderación",
    icon: "pi pi-shield",
    items: [
      { label: "Moderación de chat", to: "/chat" },
      { label: "Moderación de fotos", to: "/moderation/photo-reports" },
    ],
  },
  {
    label: "Soporte",
    icon: "pi pi-question-circle",
    items: [
      { label: "Categorías", to: "/soporte/categorias" },
      { label: "Artículos", to: "/soporte/articulos" },
      { label: "FAQs destacadas", to: "/soporte/faqs" },
      { label: "Motivos de contacto", to: "/soporte/motivos" },
      { label: "Configuración", to: "/soporte/configuracion" },
      { label: "Feedback de artículos", to: "/soporte/feedback" },
      { label: "Consultas recibidas", to: "/soporte/contactos" },
    ],
  },
  {
    label: "Agentes",
    icon: "pi pi-sparkles",
    superAdminOnly: true,
    items: [
      { label: "Consultas", to: "/agents/queries" },
      { label: "Ejemplos", to: "/agents/exemplars" },
    ],
  },
  {
    label: "Analítica",
    icon: "pi pi-chart-bar",
    items: [
      { label: "Reportes", to: "/reportes" },
      { label: "Métricas de beneficios", to: "/benefits/metrics" },
      { label: "Usuarios activos", to: "/activity/active-users" },
      { label: "Reporte de notificaciones", to: "/reportes/notificaciones" },
    ],
  },
  {
    label: "Configuración",
    icon: "pi pi-cog",
    items: [
      { label: "Gestión de tipos", to: "/config/tipo-datos" },
      { label: "Configuración de reglas", to: "/rules" },
      { label: "Feature flags", to: "/feature-flags" },
      { label: "Compartir", to: "/config/compartir" },
      { label: "Secciones del Home", to: "/config/home-sections" },
      { label: "Banners del Home", to: "/config/home-banners" },
    ],
  },
];
